import { simulateContract, writeContract } from '@wagmi/core';

import type { Address } from 'viem';

import type { LaunchpadSalePoolType } from '@api';
import { LAUNCHPAD_SALE_POOL_TYPE } from '@api';

// eslint-disable-next-line deprecate/import
import { wagmiConfig } from '@shared/common/providers/LegacyWeb3Provider/wagmi';
import {
  DEFAULT_TARGET_CHAIN,
  LAST_FCFS_CONTRACT_VERSION,
  LAST_PRO_RATA_CONTRACT_VERSION,
  LAUNCHPAD_DEPOSIT_CONTRACTS,
} from '@shared/constants/wallets';
import { getEstimateGasPrice } from '@shared/helpers/web3/getEstimateGasPrice';

export const isProRataType = (type?: LaunchpadSalePoolType) => {
  if (!type) {
    return false;
  }

  return (
    type === LAUNCHPAD_SALE_POOL_TYPE.PRO_RATA ||
    type === LAUNCHPAD_SALE_POOL_TYPE.PRO_RATA_SQRP_GATED
  );
};

const depositContractCall = async ({
  props,
  type,
}: {
  type: LaunchpadSalePoolType;
  props: {
    amount: number;
    contractAddress: Address;
    amountInWei: string;
    userId: string;
    transactionId: string;
    exchangeRateInWei: string;
    walletAddress: Address;
    timeStampLimit: number;
    feeInWei: string;
    signature: Address;
    boosted: boolean;
  };
}) => {
  const {
    contractAddress,
    amountInWei,
    userId,
    transactionId,
    walletAddress,
    timeStampLimit,
    signature,
    exchangeRateInWei,
    feeInWei,
    boosted,
  } = props;

  if (isProRataType(type)) {
    const { gasEstimate, gasPrice } = await getEstimateGasPrice({
      abi: LAUNCHPAD_DEPOSIT_CONTRACTS.PRO_RATA[LAST_PRO_RATA_CONTRACT_VERSION].abi,
      chainId: DEFAULT_TARGET_CHAIN.id,
      contractAddress: contractAddress,
      functionName: 'depositSig',
      walletAddress,
      args: [
        {
          baseAmount: BigInt(amountInWei),
          boost: boosted,
          boostExchangeRate: BigInt(exchangeRateInWei),
          transactionId,
          timestampLimit: timeStampLimit,
          fee: BigInt(feeInWei),
          signature,
        },
      ],
    });

    const { request } = await simulateContract(wagmiConfig, {
      ...LAUNCHPAD_DEPOSIT_CONTRACTS.PRO_RATA[LAST_PRO_RATA_CONTRACT_VERSION],
      address: contractAddress,
      functionName: 'depositSig',
      chainId: DEFAULT_TARGET_CHAIN.id,
      gas: gasEstimate,
      ...(gasPrice && { gasPrice }),
      args: [
        {
          baseAmount: BigInt(amountInWei),
          boost: boosted,
          boostExchangeRate: BigInt(exchangeRateInWei),
          transactionId,
          timestampLimit: timeStampLimit,
          fee: BigInt(feeInWei),
          signature,
        },
      ],
    });

    return writeContract(wagmiConfig, request);
  }

  const { gasEstimate, gasPrice } = await getEstimateGasPrice({
    abi: LAUNCHPAD_DEPOSIT_CONTRACTS.FCFS[LAST_FCFS_CONTRACT_VERSION].abi,
    chainId: DEFAULT_TARGET_CHAIN.id,
    contractAddress: contractAddress,
    functionName: 'depositSig',
    walletAddress,
    args: [
      userId,
      transactionId,
      walletAddress,
      BigInt(amountInWei),
      timeStampLimit,
      BigInt(feeInWei),
      signature,
    ],
  });

  const { request } = await simulateContract(wagmiConfig, {
    ...LAUNCHPAD_DEPOSIT_CONTRACTS.FCFS[LAST_FCFS_CONTRACT_VERSION],
    address: contractAddress,
    functionName: 'depositSig',
    chainId: DEFAULT_TARGET_CHAIN.id,
    gas: gasEstimate,
    ...(gasPrice && { gasPrice }),
    args: [
      userId,
      transactionId,
      walletAddress,
      BigInt(amountInWei),
      timeStampLimit,
      BigInt(feeInWei),
      signature,
    ],
  });

  return writeContract(wagmiConfig, request);
};

export default depositContractCall;
